import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Spinner } from "reactstrap";
import {
  IoClose,
  IoCheckmarkCircleOutline,
  IoCloseCircleOutline,
  IoBanOutline,
  IoDocumentTextOutline
} from "react-icons/io5";
import { toast } from "react-toastify";
import DefaultDrawer from "../../../../../components/Drawers/Default";
import DefaultButton from "../../../../../components/Buttons/Default";
import ConfirmationModal from "../../../../../components/Modals/Confirmation";
import { get, post } from "../../../../../utils/helpers/api";
import { AWS_S3_URL } from "../../../../../utils/constants";
import { useSettings } from "../../../../../context/settings";
import { printLeaveStatus, printLeaveType } from "../../../utils/helpers";
import "./styles.css";

const STYLES = {
  wrap:       { display: "flex", flexDirection: "column", height: "100%", background: "#fff" },
  header:     { display: "flex", alignItems: "center", gap: 8, padding: "0 16px", height: 52, borderBottom: "1px solid #eaecf0", flexShrink: 0 },
  closeBtn:   { background: "none", border: "none", cursor: "pointer", color: "#888", padding: 4, borderRadius: 6, display: "flex", alignItems: "center" },
  title:      { fontSize: 14, fontWeight: 700, color: "#1d1d1f" },
  actionBar:  { display: "flex", alignItems: "center", borderBottom: "1px solid #eaecf0", background: "#fafafa", flexShrink: 0, flexWrap: "wrap" },
  scroll:     { flex: 1, overflowY: "auto", padding: 20 },
  employee:   { display: "flex", alignItems: "center", gap: 12, padding: "14px 16px", border: "1px solid #eaecf0", borderRadius: 8, marginBottom: 20 },
  avatar:     { width: 42, height: 42, borderRadius: "50%", background: "#eef0ff", color: "#6C63FF", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, fontSize: 15, flexShrink: 0 },
  section:    { fontSize: 10, fontWeight: 700, letterSpacing: 1.2, color: "#aaa", marginBottom: 10, textTransform: "uppercase" },
  row:        { display: "flex", alignItems: "flex-start", gap: 12, padding: "7px 0", fontSize: 13 },
  rowLabel:   { width: 150, flexShrink: 0, color: "#999", fontWeight: 600 },
  rowValue:   { color: "#333", fontWeight: 500, wordBreak: "break-word" },
  block:      { marginTop: 24, paddingTop: 18, borderTop: "1px solid #eee" },
  text:       { fontSize: 13, color: "#555", whiteSpace: "pre-wrap", margin: 0, lineHeight: 1.7 },
  file:       { display: "inline-flex", alignItems: "center", gap: 8, fontSize: 13, fontWeight: 600, color: "#6C63FF", textDecoration: "none", border: "1px solid #eaecf0", borderRadius: 8, padding: "9px 12px" },
  muted:      { fontSize: 13, color: "#aaa", margin: 0 }
};

function DetailRow({ label, children }) {
  return (
    <div style={STYLES.row}>
      <div style={STYLES.rowLabel}>{label}</div>
      <div style={STYLES.rowValue}>{children || "—"}</div>
    </div>
  );
}

function userName(user) {
  if (!user) return null;
  return `${user.firstname ?? ""} ${user.lastname ?? ""}`.trim();
}

function fileName(path) {
  if (!path) return "";
  const name = path.split("/").pop();
  // stored as {uuid}_{original name}
  const parts = name.split("_");
  return parts.length > 1 ? parts.slice(1).join("_") : name;
}

export default function ViewDrawer({ leaveRequestId, isOpen, onClose, refreshLeaveRequests }) {
  const { t } = useTranslation();
  const { formatDate } = useSettings();
  const [leaveRequest, setLeaveRequest] = useState(null);
  const [loading, setLoading] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [statusToConfirm, setStatusToConfirm] = useState(null);

  const getLeaveRequest = async () => {
    const data = await get(`/hr/leave-requests/view/${leaveRequestId}`);
    if (data?.leave_request) {
      setLeaveRequest(data.leave_request);
    } else {
      toast.error(data?.message || t('hr.failedLoadLeaveRequest'));
    }
    setLoading(false);
  };

  useEffect(() => {
    if (!leaveRequestId) return;
    setLoading(true);
    setLeaveRequest(null);
    getLeaveRequest();
  }, [leaveRequestId]);

  const CONFIRMATIONS = {
    approved: {
      title: t('hr.approveLeaveRequest'),
      message: t('hr.approveLeaveRequestConfirm'),
      button: t('hr.approve'),
      color: 'success',
      success: t('hr.leaveRequestApproved')
    },
    rejected: {
      title: t('hr.rejectLeaveRequest'),
      message: t('hr.rejectLeaveRequestConfirm'),
      button: t('hr.reject'),
      color: 'danger',
      success: t('hr.leaveRequestRejected')
    },
    cancelled: {
      title: t('hr.cancelLeaveRequest'),
      message: t('hr.cancelLeaveRequestConfirm'),
      button: t('hr.cancelRequest'),
      color: 'danger',
      success: t('hr.leaveRequestCancelled')
    }
  };

  const updateStatus = async (status) => {
    setUpdating(true);
    const data = await post(`/hr/leave-requests/update-status/${leaveRequestId}`, { status });
    if (data?.success) {
      setLeaveRequest(data.leave_request);
      refreshLeaveRequests?.();
      toast.success(CONFIRMATIONS[status].success);
    } else {
      toast.error(data?.message || t('common.somethingWentWrong'));
    }
    setUpdating(false);
  };

  const employee = leaveRequest?.employee;
  const employeeName = employee ? `${employee.firstname ?? ""} ${employee.lastname ?? ""}`.trim() : "";
  const initials = employeeName ? employeeName.split(" ").filter(Boolean).slice(0, 2).map(w => w[0].toUpperCase()).join("") : "—";

  return (
    <DefaultDrawer isOpen={isOpen} size={620} toggle={onClose}>
      <div style={STYLES.wrap}>
        {/* ── Header ── */}
        <div style={STYLES.header}>
          <button
            style={STYLES.closeBtn}
            onClick={onClose}
            onMouseEnter={e => { e.currentTarget.style.background = "#f0f0f0"; e.currentTarget.style.color = "#333"; }}
            onMouseLeave={e => { e.currentTarget.style.background = "none"; e.currentTarget.style.color = "#888"; }}
          >
            <IoClose size={20} />
          </button>
          <span style={STYLES.title}>{t('hr.leaveRequest')}</span>
          {leaveRequest && printLeaveStatus(leaveRequest.status)}
        </div>

        {/* ── Action Bar ── */}
        {leaveRequest && leaveRequest.status === 'pending' &&
          <div style={STYLES.actionBar}>
            <DefaultButton
              className="btn-action"
              size="sm"
              color="light"
              outline
              disabled={!leaveRequest || updating || leaveRequest.status === 'approved'}
              onClick={() => setStatusToConfirm('approved')}
            >
              <IoCheckmarkCircleOutline size={15} className="text-success" />
              {t('hr.approve')}
            </DefaultButton>
            <DefaultButton
              className="btn-action"
              size="sm"
              color="light"
              outline
              disabled={!leaveRequest || updating || leaveRequest.status === 'rejected'}
              onClick={() => setStatusToConfirm('rejected')}
            >
              <IoCloseCircleOutline size={15} className="text-danger" />
              {t('hr.reject')}
            </DefaultButton>
            <DefaultButton
              className="btn-action"
              size="sm"
              color="light"
              outline
              disabled={!leaveRequest || updating || leaveRequest.status === 'cancelled'}
              onClick={() => setStatusToConfirm('cancelled')}
            >
              <IoBanOutline size={15} className="text-secondary" />
              {t('hr.cancelRequest')}
            </DefaultButton>
          </div>
        }

        {/* ── Content ── */}
        <div style={STYLES.scroll}>
          {loading || !leaveRequest ?
            <Spinner color="primary" className="d-block mx-auto my-4" />
          :
            <>
              <div style={STYLES.employee}>
                <div style={STYLES.avatar}>{initials}</div>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 14, color: "#1d1d1f" }}>{employeeName || "—"}</div>
                  <div style={{ fontSize: 12, color: "#888", marginTop: 2 }}>
                    {[employee?.job_title, employee?.department].filter(Boolean).join(" · ") || "—"}
                  </div>
                </div>
              </div>

              <div style={STYLES.section}>{t('hr.requestDetails')}</div>
              <DetailRow label={t('hr.leaveType')}>{printLeaveType(leaveRequest.leave_type)}</DetailRow>
              <DetailRow label={t('hr.startDate')}>{formatDate(leaveRequest.start_date)}</DetailRow>
              <DetailRow label={t('hr.endDate')}>{formatDate(leaveRequest.end_date)}</DetailRow>
              <DetailRow label={t('hr.totalDays')}>
                {leaveRequest.total_days ? `${leaveRequest.total_days} ${t('hr.days')}` : null}
              </DetailRow>
              <DetailRow label={t('hr.orderDate')}>
                {formatDate(leaveRequest.order_date || leaveRequest.created_at)}
              </DetailRow>

              <div style={STYLES.block}>
                <div style={STYLES.section}>{t('hr.reason')}</div>
                {leaveRequest.reason ?
                  <p style={STYLES.text}>{leaveRequest.reason}</p>
                :
                  <p style={STYLES.muted}>{t('hr.noReason')}</p>
                }
              </div>

              <div style={STYLES.block}>
                <div style={STYLES.section}>{t('hr.attachment')}</div>
                {leaveRequest.file_path ?
                  <a
                    style={STYLES.file}
                    href={AWS_S3_URL + leaveRequest.file_path}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <IoDocumentTextOutline size={18} />
                    {fileName(leaveRequest.file_path)}
                  </a>
                :
                  <p style={STYLES.muted}>{t('hr.noAttachment')}</p>
                }
              </div>

              <div style={STYLES.block}>
                <div style={STYLES.section}>{t('hr.activity')}</div>
                <DetailRow label={t('hr.requestedBy')}>
                  {[userName(leaveRequest.created_by), formatDate(leaveRequest.created_at)].filter(Boolean).join(" · ")}
                </DetailRow>
                {leaveRequest.approved_date && (
                  <DetailRow label={t('hr.approvedBy')}>
                    {[userName(leaveRequest.approved_by), formatDate(leaveRequest.approved_date)].filter(Boolean).join(" · ")}
                  </DetailRow>
                )}
                {leaveRequest.rejected_date && (
                  <DetailRow label={t('hr.rejectedBy')}>
                    {[userName(leaveRequest.rejected_by), formatDate(leaveRequest.rejected_date)].filter(Boolean).join(" · ")}
                  </DetailRow>
                )}
                {leaveRequest.cancelled_date && (
                  <DetailRow label={t('hr.cancelledBy')}>
                    {[userName(leaveRequest.cancelled_by), formatDate(leaveRequest.cancelled_date)].filter(Boolean).join(" · ")}
                  </DetailRow>
                )}
              </div>
            </>
          }
        </div>
      </div>

      <ConfirmationModal
        isOpen={statusToConfirm !== null}
        title={statusToConfirm ? CONFIRMATIONS[statusToConfirm].title : ''}
        message={statusToConfirm ? CONFIRMATIONS[statusToConfirm].message : ''}
        confirmationText={statusToConfirm ? CONFIRMATIONS[statusToConfirm].button : ''}
        confirmColor={statusToConfirm ? CONFIRMATIONS[statusToConfirm].color : 'danger'}
        zIndex={9999}
        onConfirm={() => updateStatus(statusToConfirm)}
        toggle={() => setStatusToConfirm(null)}
      />
    </DefaultDrawer>
  );
}
